import axios, { AxiosError, AxiosResponse } from 'axios'

import { Message, MessageSend } from '#/entities/message'
import { getApiUrl } from '#/shared/lib/constants'
import { IMessageRequest } from '#/shared/lib/types/types-gpt'

export async function parseStreamErrorResponse(response: Response): Promise<string> {
	try {
		const body = (await response.json()) as { detail?: string }
		return body.detail ?? 'Непредвиденная ошибка, попробуйте еще раз'
	} catch {
		return 'Непредвиденная ошибка, попробуйте еще раз'
	}
}

function sanitizeContent(content: string): string {
	return content.replace(/\r\n|\r|\n/g, ' ').replace(/ {2,}/g, ' ').trim()
}

export const chatMessagesApi = {
	getMessages: async (chatUid: string, offset: number, token?: string) => {
		try {
			const { data } = await axios.get<Message[]>(getApiUrl() + `/chats/${chatUid}/messages/?limit=10&offset=${offset}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			return data
		} catch (err) {
			return {
				error: true as const,
				message: 'Произошла ошибка при выполнении запроса',
				details: err as AxiosError,
			}
		}
	},

	deleteMessage: (chatUid: string, messageUid: string, token?: string) =>
		axios.delete(getApiUrl() + `/chats/${chatUid}/messages/${messageUid}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		}),

	sendMessage: async <T>(chatUid: string, dataForSend: MessageSend<T> | FormData, token?: string) => {
		const headerDataType = dataForSend instanceof FormData ? 'multipart/form-data' : 'application/json'
		const payload =
			dataForSend instanceof FormData
				? dataForSend
				: { ...dataForSend, content: sanitizeContent(dataForSend.content) }

		if (payload instanceof FormData && payload.has('content')) {
			const raw = payload.get('content')
			if (typeof raw === 'string') {
				payload.set('content', sanitizeContent(raw))
			}
		}

		return axios.post<IMessageRequest, AxiosResponse<Message[] | { detail: string }>>(
			getApiUrl() + `/chats/${chatUid}/messages/`,
			payload,
			{
				withCredentials: true,
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': headerDataType,
				},
			}
		)
	},

	sendMessageStream: <T>(chatUid: string, dataForSend: MessageSend<T> | FormData, token: string, signal?: AbortSignal) => {
		const url = `${getApiUrl() + '/api'}/chats/${chatUid}/messages/stream`
		const streamHeaders = {
			Authorization: `Bearer ${token}`,
			Accept: 'text/event-stream',
		}

		if (dataForSend instanceof FormData) {
			if (dataForSend.has('content')) {
				const raw = dataForSend.get('content')
				if (typeof raw === 'string') {
					dataForSend.set('content', sanitizeContent(raw))
				}
			}
			return fetch(url, {
				method: 'POST',
				headers: streamHeaders,
				body: dataForSend,
				signal,
				credentials: 'include',
			})
		}

		const { info } = dataForSend
		const content = sanitizeContent(dataForSend.content)

		return fetch(url, {
			method: 'POST',
			headers: {
				...streamHeaders,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ content, info }),
			signal,
			credentials: 'include',
		})
	},

	reconnectMessageStream: (chatUid: string, offset: number, token: string, signal?: AbortSignal) => {
		const baseUrl = `${getApiUrl() + '/api'}/chats/${chatUid}/messages/stream`
		const url = offset > 0 ? `${baseUrl}?offset=${offset}` : baseUrl

		return fetch(url, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${token}`,
				Accept: 'text/event-stream',
			},
			signal,
			credentials: 'include',
		})
	},
}
